import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

type GenerateApiKeyRequest = components['schemas']['GenerateApiKeyEndpoint_Request'];
type GenerateApiKeyResponse = components['schemas']['GenerateApiKeyEndpoint_Response'];
type GenerateApiKeyInput = { organizationId: number } & GenerateApiKeyRequest;

export function useGenerateApiKey() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: GenerateApiKeyInput): Promise<GenerateApiKeyResponse> => {
      const client = createApiClient(getAccessToken);
      const { data } = await client.POST('/api/organizations/{organizationId}/api-keys', {
        params: { path: { organizationId: input.organizationId } },
        body: { system: input.system },
      });

      if (!data) {
        throw new Error('No data returned from API');
      }

      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys', variables.organizationId] });
    },
  });

  return {
    generateApiKey: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error instanceof Error ? mutation.error.message : null,
    resetError: mutation.reset,
  };
}
